import React from 'react';
import { Link } from 'react-router-dom';
import { Star, BookOpen, Award, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const DEFAULT_AVATAR = 'https://ui-avatars.com/api/?name=Tutor&background=0D8ABC&color=fff&size=200';

const TutorCard = ({ tutor }) => {
  const { customer } = useCustomerAuth();

  const getTutorName = () => tutor.name || 'Tutor';
  
  const getSubjects = () => {
    const subjects = tutor.subjects || [];
    if (subjects.length > 0 && typeof subjects[0] === 'object') {
      return subjects.map(s => s.name || s.subject_name || s);
    }
    return subjects;
  };
  
  const getExperience = () => tutor.experience_years || tutor.experience || 0;
  
  const getRating = () => tutor.rating || 0;
  
  const getReviews = () => tutor.total_reviews || tutor.reviews || 0;
  
  const getProfileImage = () => {
    if (tutor.profile_image) {
      return tutor.profile_image;
    }
    const name = encodeURIComponent(getTutorName());
    return `https://ui-avatars.com/api/?name=${name}&background=0D8ABC&color=fff&size=200`;
  };
  
  const getClasses = () => {
    const classes = tutor.classes;
    if (Array.isArray(classes) && classes.length > 0) {
      const classNumbers = classes.map(c => {
        if (typeof c === 'string') {
          const match = c.match(/\d+/);
          return match ? match[0] : c;
        }
        return c;
      });
      return classNumbers.slice(0, 3).join(', ') + (classNumbers.length > 3 ? '...' : '');
    }
    return 'All';
  };
  
  const getBio = () => tutor.bio || '';
  
  const getTutorId = () => tutor.id;
  
  const getTutorCode = () => tutor.tutor_id_code || tutor.id;

  const subjects = getSubjects();
  const isLoggedIn = !!customer;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group bg-white flex flex-col h-full">
      <div className="relative overflow-hidden h-48 sm:h-56 bg-gradient-to-br from-blue-50 to-teal-50">
        <img
          src={getProfileImage()}
          alt={getTutorName()}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={(e) => {
            const name = encodeURIComponent(getTutorName());
            e.target.src = `https://ui-avatars.com/api/?name=${name}&background=0D8ABC&color=fff&size=200`;
          }}
        />
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-3 rounded-full flex items-center space-x-1 shadow-lg">
          <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-500 fill-yellow-500" />
          <span className="text-xs sm:text-sm font-semibold text-gray-900">{getRating().toFixed(1)}</span>
          <span className="text-xs text-gray-600">({getReviews()})</span>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 flex-1 flex flex-col">
        <div>
          <Link
            to={`/tutor-profile/${getTutorCode()}`}
            className="text-lg sm:text-xl font-bold text-gray-900 mb-1 hover:text-blue-600 transition-colors block"
          >
            {getTutorName()}
          </Link>
          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{getBio()}</p>
        </div>

        {subjects.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {subjects.slice(0, 3).map((subject, index) => (
              <span
                key={index}
                className="px-2 py-0.5 sm:px-3 sm:py-1 bg-gradient-to-r from-blue-50 to-teal-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200"
              >
                {subject}
              </span>
            ))}
            {subjects.length > 3 && (
              <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                +{subjects.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-1">
            <Award className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            <span>{getExperience()} years exp</span>
          </div>
          <div className="flex items-center space-x-1">
            <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 text-teal-600" />
            <span>Class {getClasses()}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2 mt-auto">
          {/* NOTE: "Book Demo" and "Submit Query" features are temporarily on hold. 
                   Customers can only browse tutors and view profiles.
                   Code kept for future use.
          {isLoggedIn ? (
            <Link
              to="/customer/teacher-query"
              className="flex-1"
            >
              <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white transition-all duration-300 text-sm sm:text-base">
                Submit Query
              </Button>
            </Link>
          ) : (
            <Link
              to="/customer/login"
              className="flex-1"
            >
              <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white transition-all duration-300 text-sm sm:text-base">
                Login to Submit Query
              </Button>
            </Link>
          )}
          */}
          <Link
            to={`/tutor-profile/${getTutorCode()}`}
            className="flex-1"
          >
            <Button variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors duration-200 text-sm sm:text-base">
              <User className="h-4 w-4 mr-1" />
              View Profile
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default TutorCard;
